// src/scenes/global/Sidebar.jsx
import React, { useState } from "react";
import logo from "../../assets/fig_CROP.png";
import { Box, IconButton, Typography, Collapse, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ProSidebar, Menu, MenuItem as SidebarMenuItem } from "react-pro-sidebar";
import "react-pro-sidebar/dist/css/styles.css";
import { Link, useNavigate } from "react-router-dom";
import { tokens } from "../../theme";
/* ----------------------------- ICON IMPORTS ----------------------------- */
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import LunchDiningIcon from "@mui/icons-material/LunchDining";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import PsychologyIcon from "@mui/icons-material/Psychology";
import CategoryIcon from "@mui/icons-material/Category";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ListAltIcon from "@mui/icons-material/ListAlt";
import InventoryIcon from "@mui/icons-material/Inventory";
import AssessmentIcon from "@mui/icons-material/Assessment";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import PeopleIcon from "@mui/icons-material/People";
import RoomIcon from "@mui/icons-material/Room";
import HomeIcon from "@mui/icons-material/PersonSearch"; // (kept as-is from your file)
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import LogoutIcon from "@mui/icons-material/Logout";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DashboardMealIcon from "@mui/icons-material/Dashboard"; // reuse dashboard icon

/* ----------------------------- NEW ICONS ADDED ----------------------------- */
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";        // NEW: Home link icon
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";          // NEW: Mobile Inspection
import ConstructionIcon from "@mui/icons-material/Construction";        // NEW: Inspection Builder
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard";    // NEW: Inspection Dashboard
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";          // NEW: POS À-la-Carte Import

import { useAuth } from "../../context/AuthContext";

/* ----------------------------------------------------------------------- */
/* COLOR PALETTE                                                           */
/* ----------------------------------------------------------------------- */
const colorsMap = {
    expense: "#FF6347",
    income: "#32CD32",
    budget: "#1E90FF",
    docScan: "#FFD700",
    vendor: "#FF8C00",
    inventory: "#A0522D", // used for Inventory section
    reports: "#20B2AA",
    admin: "#9370DB",
    ai: "#FF1493",
    default: "#868dfb",
};

/* ----------------------------------------------------------------------- */
/* REUSABLE SIDEBAR ITEM COMPONENT                                         */
/* ----------------------------------------------------------------------- */
const Item = ({ title, to, icon, selected, setSelected, tint = colorsMap.default }) => {
    const theme = useTheme();
    const themeColors = tokens(theme.palette.mode);
    return (
        <SidebarMenuItem
            active={selected === title}
            style={{ paddingLeft: 40 }}
            onClick={() => setSelected(title)}
            icon={React.cloneElement(icon, { sx: { color: tint } })}
        >
            <Typography color={themeColors.grey[100]}>{title}</Typography>
            <Link to={to} />
        </SidebarMenuItem>
    );
};

/* =============================================================================
   MAIN <Sidebar /> COMPONENT
============================================================================= */
const Sidebar = () => {
    const theme = useTheme();
    const themeColors = tokens(theme.palette.mode);
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    const isAdmin = user?.RoleId === 1;
    const isSchoolUser = user?.RoleId === 2;

    const [isCollapsed, setIsCollapsed] = useState(false);
    const [selected, setSelected] = useState("");
    const [open, setOpen] = useState({
        dashboards: false,
        managersDashboards: false,
        managersSection: false,
        expense: false,
        income: false,
        funding: false,
        budget: false,
        vendor: false,
        inventory: false, // controls Inventory section
        reports: false,
        reportsKpi: false,
        employees: false,
        sandbox: false,
    });

    const toggle = (sec) => setOpen((o) => ({ ...o, [sec]: !o[sec] }));
    const handleLogout = () => {
        logout();
        navigate("/login");
    };

    // ─── ROLEID=2 ─── Manager sees ONLY manager menu
    if (isSchoolUser) {
        return (
            <Box
                sx={{
                    "& .pro-sidebar-inner": { background: `${themeColors.primary[400]} !important` },
                    "& .pro-icon-wrapper": { backgroundColor: "transparent !important" },
                    "& .pro-inner-item": { padding: "5px 35px 5px 20px !important" },
                    "& .pro-inner-item:hover": { color: colorsMap.default + " !important" },
                    "& .pro-menu-item.active": { color: "#6870fa !important" },
                }}
            >
                <ProSidebar collapsed={isCollapsed} style={{ width: isCollapsed ? 80 : 250 }}>
                    <Menu iconShape="square">
                        {/* LOGO */}
                        {!isCollapsed ? (
                            <Box display="flex" justifyContent="center" alignItems="center" p={2}>
                                <img src={logo} alt="FIG Logo" style={{ width: 80, height: 80 }} />
                            </Box>
                        ) : (
                            <Box display="flex" justifyContent="center" alignItems="center" p={1}>
                                <img src={logo} alt="FIG Logo" style={{ width: 40, height: 40 }} />
                            </Box>
                        )}

                        {/* COLLAPSE */}
                        <SidebarMenuItem
                            onClick={() => setIsCollapsed(!isCollapsed)}
                            icon={<MenuOutlinedIcon sx={{ color: colorsMap.default }} />}
                            style={{ margin: "10px 0 20px 0", color: themeColors.grey[100] }}
                        >
                            {!isCollapsed && (
                                <Box ml={2} display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="h3" color={themeColors.grey[100]}>
                                        FIG Finance
                                    </Typography>
                                    <IconButton>
                                        <MenuOutlinedIcon sx={{ color: colorsMap.default }} />
                                    </IconButton>
                                </Box>
                            )}
                        </SidebarMenuItem>

                        {/* MANAGERS DASHBOARD */}
                        <SidebarMenuItem
                            icon={<DashboardMealIcon sx={{ color: colorsMap.income }} />}
                            onClick={() => toggle("managersDashboards")}
                            style={{ color: themeColors.grey[300], paddingLeft: 20 }}
                        >
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6">Managers Dashboard</Typography>
                                {open.managersDashboards ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </Box>
                        </SidebarMenuItem>
                        <Collapse in={open.managersDashboards}>
                            <Item title="Site Profit Loss" to="/sitemanager/DashboardProfitLoss" icon={<DashboardIcon />} selected={selected} setSelected={setSelected} tint={colorsMap.income} />
                            <Item title="Site Participation" to="/sitemanager/DashboardADP" icon={<DashboardIcon />} selected={selected} setSelected={setSelected} tint={colorsMap.income} />
                            <Item title="Meal Count Dashboard" to="/sitemanager/DashboardMealCount" icon={<DashboardIcon />} selected={selected} setSelected={setSelected} tint={colorsMap.income} />
                            <Item title="MPLH Dashboard" to="/sitemanager/DashboardMPLH" icon={<DashboardIcon />} selected={selected} setSelected={setSelected} tint={colorsMap.income} />
                            <Item title="I&E per MEQ" to="/sitemanager/income_expenseMEQ" icon={<DashboardIcon />} selected={selected} setSelected={setSelected} tint={colorsMap.income} />
                        </Collapse>
                        <Divider sx={{ my: 1 }} />

                        {/* MANAGERS SECTION */}
                        <SidebarMenuItem
                            icon={<SupervisorAccountIcon sx={{ color: colorsMap.admin }} />}
                            onClick={() => toggle("managersSection")}
                            style={{ color: themeColors.grey[300], paddingLeft: 20 }}
                        >
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6">Managers Section</Typography>
                                {open.managersSection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </Box>
                        </SidebarMenuItem>
                        <Collapse in={open.managersSection}>
                            <Item title="AI Invoice Upload" to="/sitemanager/ai_invoices" icon={<PsychologyIcon />} selected={selected} setSelected={setSelected} tint={colorsMap.ai} />
                            <Item title="Day End" to="/sitemanager/DayEnd" icon={<PsychologyIcon />} selected={selected} setSelected={setSelected} tint={colorsMap.ai} />
                            <Item title="Expenses" to="/sitemanager/Expenses" icon={<ReceiptOutlinedIcon />} selected={selected} setSelected={setSelected} tint={colorsMap.expense} />
                            <Item title="Income" to="/sitemanager/Incomes" icon={<AttachMoneyIcon />} selected={selected} setSelected={setSelected} tint={colorsMap.income} />
                            <Item title="Bulk Count Entry" to="/sitemanager/MealCountEntry" icon={<LunchDiningIcon />} selected={selected} setSelected={setSelected} tint={colorsMap.income} />
                            <Item title="Bulk Labor Entry" to="/sitemanager/LaborEntry" icon={<PersonOutlinedIcon />} selected={selected} setSelected={setSelected} tint={colorsMap.reports} />
                        </Collapse>
                        <Divider sx={{ my: 1 }} />

                        {/* FIG AGENT */}
                        <SidebarMenuItem
                            icon={<PersonSearchIcon sx={{ color: colorsMap.ai }} />}
                            onClick={() => toggle("sandbox")}
                            style={{ color: themeColors.grey[300], paddingLeft: 20 }}
                        >
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6">FIG Agent</Typography>
                                {open.sandbox ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </Box>
                        </SidebarMenuItem>
                        <Collapse in={open.sandbox}>
                            <Item title="Productivity Chat" to="/figagent" icon={<PsychologyIcon />} selected={selected} setSelected={setSelected} tint={colorsMap.ai} />
                        </Collapse>
                        <Divider sx={{ my: 1 }} />

                        {/* LOGOUT */}
                        <SidebarMenuItem
                            icon={<LogoutIcon sx={{ color: colorsMap.expense }} />}
                            onClick={handleLogout}
                            style={{ color: themeColors.grey[300], paddingLeft: 20, marginTop: "auto" }}
                        >
                            <Typography variant="h6">Logout</Typography>
                        </SidebarMenuItem>
                    </Menu>
                </ProSidebar>
            </Box>
        );
    }

    // ─── ALL OTHER USERS ─── show full sidebar (unchanged + requested additions)
    return (
        <Box
            sx={{
                "& .pro-sidebar-inner": { background: `${themeColors.primary[400]} !important` },
                "& .pro-icon-wrapper": { backgroundColor: "transparent !important" },
                "& .pro-inner-item": { padding: "5px 35px 5px 20px !important" },
                "& .pro-inner-item:hover": { color: colorsMap.default + " !important" },
                "& .pro-menu-item.active": { color: "#6870fa !important" },
            }}
        >
            <ProSidebar collapsed={isCollapsed} style={{ width: isCollapsed ? 80 : 250 }}>
                <Menu iconShape="square">
                    {/* LOGO, COLLAPSE BUTTON, USER INFO */}
                    {!isCollapsed ? (
                        <Box display="flex" justifyContent="center" alignItems="center" p={2}>
                            <img src={logo} alt="FIG Logo" style={{ width: 80, height: 80 }} />
                        </Box>
                    ) : (
                        <Box display="flex" justifyContent="center" alignItems="center" p={1}>
                            <img src={logo} alt="FIG Logo" style={{ width: 40, height: 40 }} />
                        </Box>
                    )}
                    <SidebarMenuItem
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        icon={<MenuOutlinedIcon sx={{ color: colorsMap.default }} />}
                        style={{ margin: "10px 0 20px 0", color: themeColors.grey[100] }}
                    >
                        {!isCollapsed && (
                            <Box display="flex" justifyContent="space-between" alignItems="center" ml={2}>
                                <Typography variant="h3" color={themeColors.grey[100]}>
                                    FIG Finance
                                </Typography>
                                <IconButton>
                                    <MenuOutlinedIcon sx={{ color: colorsMap.default }} />
                                </IconButton>
                            </Box>
                        )}
                    </SidebarMenuItem>

                    {!isCollapsed && user && (
                        <Box mb={1} textAlign="center">
                            <Typography variant="subtitle2" color={themeColors.grey[300]}>
                                Role ID: {user.RoleId}
                            </Typography>
                        </Box>
                    )}
                    {!isCollapsed && user && (
                        <Box mb={2} textAlign="center">
                            <Typography variant="h6" color={themeColors.grey[100]}>
                                Welcome, {user.username}
                            </Typography>
                            <Typography variant="body2" color={themeColors.grey[300]}>
                                District: {user.schoolDistrictName || "Unknown"}
                            </Typography>
                        </Box>
                    )}

                    {/* ─── NEW: Home link at very top ─── */}
                    <Item
                        title="Home"
                        to="/home"
                        icon={<HomeOutlinedIcon />}
                        selected={selected}
                        setSelected={setSelected}
                        tint={colorsMap.default}
                    />
                    <Divider sx={{ my: 1 }} />

                    {/* ─── Dashboards ─── */}
                    <SidebarMenuItem
                        icon={<DashboardIcon sx={{ color: colorsMap.budget }} />}
                        onClick={() => toggle("dashboards")}
                        style={{ color: themeColors.grey[300], paddingLeft: 20 }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Dashboards</Typography>
                            {open.dashboards ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </Box>
                    </SidebarMenuItem>
                    <Collapse in={open.dashboards}>
                        {/* Core dashboards */}
                        <Item title="Profit Loss" to="/dashboard" icon={<DashboardIcon />} selected={selected} setSelected={setSelected} />
                        <Item title="Meal Counts" to="/dashboardmealcounts" icon={<LunchDiningIcon />} selected={selected} setSelected={setSelected} />

                        {/* Remaining dashboards */}
                        <Item title="Federal Programs" to="/dashboardreimbursement" icon={<MoneyOffIcon />} tint={colorsMap.expense} selected={selected} setSelected={setSelected} />
                        <Item title="Reimbursement" to="/dashboardincomreimbursement" icon={<MonetizationOnIcon />} tint={colorsMap.income} selected={selected} setSelected={setSelected} />
                        <Item title="Payments" to="/dashboardpayments" icon={<AttachMoneyIcon />} tint={colorsMap.income} selected={selected} setSelected={setSelected} />
                        <Item title="Student Dashboard" to="/studentdashboard" icon={<PersonOutlinedIcon />} selected={selected} setSelected={setSelected} />
                    </Collapse>
                    <Divider sx={{ my: 1 }} />

                    {/* KPI Sub-section */}
                    <SidebarMenuItem
                        icon={<TrendingUpIcon sx={{ color: colorsMap.reports }} />}
                        onClick={() => toggle("dashboardsKpi")}
                        style={{ color: themeColors.grey[300], paddingLeft: 20 }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">KPI</Typography>
                            {open.dashboardsKpi ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </Box>
                    </SidebarMenuItem>
                    <Collapse in={open.dashboardsKpi}>
                        <Item title="Participation" to="/dashboard_adp" icon={<ListAltIcon />} tint={colorsMap.reports} selected={selected} setSelected={setSelected} />
                        <Item title="Meal Per Labor Hr" to="/kpi_mealsperlaborhour" icon={<LunchDiningIcon />} tint={colorsMap.reports} selected={selected} setSelected={setSelected} />
                        <Item title="Labor Entry" to="/kpi_labor" icon={<PersonOutlinedIcon />} tint={colorsMap.reports} selected={selected} setSelected={setSelected} />
                        <Item title="Site Rank" to="/dashboardsitereportcard" icon={<ListAltIcon />} tint={colorsMap.reports} selected={selected} setSelected={setSelected} />
                        <Item title="I&E per MEQ" to="/dashboard_ie_meq" icon={<ListAltIcon />} tint={colorsMap.reports} selected={selected} setSelected={setSelected} />
                    </Collapse>
                    <Divider sx={{ my: 1 }} />

                    {/* EXPENSE */}
                    <SidebarMenuItem
                        icon={<ReceiptOutlinedIcon sx={{ color: colorsMap.expense }} />}
                        onClick={() => toggle("expense")}
                        style={{ color: themeColors.grey[300], paddingLeft: 20 }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Expense</Typography>
                            {open.expense ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </Box>
                    </SidebarMenuItem>
                    <Collapse in={open.expense}>
                        <Item title="AI Invoice Import" to="/invoice_entry" icon={<QrCodeScannerIcon />} tint={colorsMap.docScan} selected={selected} setSelected={setSelected} />
                        <Item title="Expense Review" to="/finance-expenses" icon={<ReceiptOutlinedIcon />} tint={colorsMap.expense} selected={selected} setSelected={setSelected} />
                        <Item title="Price Variance" to="/bidcomparison" icon={<PsychologyIcon />} tint={colorsMap.ai} selected={selected} setSelected={setSelected} />
                        <Item title="AI Labor Import" to="/datahub/LaborHours" icon={<PsychologyIcon />} tint={colorsMap.income} selected={selected} setSelected={setSelected} />
                    </Collapse>
                    <Divider sx={{ my: 1 }} />

                    {/* INCOME */}
                    <SidebarMenuItem
                        icon={<AttachMoneyIcon sx={{ color: colorsMap.income }} />}
                        onClick={() => toggle("income")}
                        style={{ color: themeColors.grey[300], paddingLeft: 20 }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Income</Typography>
                            {open.income ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </Box>
                    </SidebarMenuItem>
                    <Collapse in={open.income}>
                        <Item title="Income Review" to="/finance-incomes" icon={<AttachMoneyIcon />} tint={colorsMap.income} selected={selected} setSelected={setSelected} />
                        <Item title="Meals Manual Entry" to="/mealcountsentry" icon={<LunchDiningIcon />} tint={colorsMap.income} selected={selected} setSelected={setSelected} />
                        <Item title="AI Meals Import" to="/datahub/MealCounts" icon={<PsychologyIcon />} tint={colorsMap.income} selected={selected} setSelected={setSelected} />
                        {/* NEW: POS A-la-Carte Import (requested) */}
                        <Item
                            title="AI POS À-la-Carte Import"
                            to="/datahub/primeroalacarteupload"
                            icon={<PointOfSaleIcon />}
                            tint={colorsMap.income}
                            selected={selected}
                            setSelected={setSelected}
                        />
                    </Collapse>
                    <Divider sx={{ my: 1 }} />

                    {/* FUNDING / COLLECTIONS */}
                    <SidebarMenuItem
                        icon={<AccountBalanceWalletIcon sx={{ color: colorsMap.income }} />}
                        onClick={() => toggle("funding")}
                        style={{ color: themeColors.grey[300], paddingLeft: 20 }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Funding / Collections</Typography>
                            {open.funding ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </Box>
                    </SidebarMenuItem>
                    <Collapse in={open.funding}>
                        <Item title="Negative Balances" to="/negativebalance" icon={<MoneyOffIcon />} tint={colorsMap.expense} selected={selected} setSelected={setSelected} />
                        <Item title="Student Payments" to="/students" icon={<PersonOutlinedIcon />} tint={colorsMap.income} selected={selected} setSelected={setSelected} />
                        <Item title="AngelFund Builder" to="/angelfundconfig" icon={<VolunteerActivismIcon />} tint={colorsMap.income} selected={selected} setSelected={setSelected} />
                        <Item title="AngelFund Link" to="/angelfund" icon={<VolunteerActivismIcon />} tint={colorsMap.income} selected={selected} setSelected={setSelected} />
                    </Collapse>
                    <Divider sx={{ my: 1 }} />

                    {/* BUDGET */}
                    <SidebarMenuItem
                        icon={<CategoryIcon sx={{ color: colorsMap.budget }} />}
                        onClick={() => toggle("budget")}
                        style={{ color: themeColors.grey[300], paddingLeft: 20 }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Budget</Typography>
                            {open.budget ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </Box>
                    </SidebarMenuItem>
                    <Collapse in={open.budget}>
                        <Item title="Budget Categories" to="/categories" icon={<CategoryIcon />} tint={colorsMap.budget} selected={selected} setSelected={setSelected} />
                        <Item title="Budget Worksheet" to="/budgetworksheet" icon={<CategoryIcon />} tint={colorsMap.budget} selected={selected} setSelected={setSelected} />
                        <Item title="Expense Budget" to="/budget-expense" icon={<AccountBalanceWalletIcon />} tint={colorsMap.budget} selected={selected} setSelected={setSelected} />
                        <Item title="Income Goals" to="/budget-income" icon={<TrendingUpIcon />} tint={colorsMap.income} selected={selected} setSelected={setSelected} />
                    </Collapse>
                    <Divider sx={{ my: 1 }} />

                    {/* VENDORS */}
                    <SidebarMenuItem
                        icon={<BusinessOutlinedIcon sx={{ color: colorsMap.vendor }} />}
                        onClick={() => toggle("vendor")}
                        style={{ color: themeColors.grey[300], paddingLeft: 20 }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Vendors</Typography>
                            {open.vendor ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </Box>
                    </SidebarMenuItem>
                    <Collapse in={open.vendor}>
                        <Item title="Vendors" to="/vendors" icon={<BusinessOutlinedIcon />} tint={colorsMap.vendor} selected={selected} setSelected={setSelected} />
                        <Item title="Vendor Products" to="/vendor-products" icon={<ListAltIcon />} tint={colorsMap.vendor} selected={selected} setSelected={setSelected} />
                        <Item title="Blanket PO" to="/vendor-po" icon={<InventoryIcon />} tint={colorsMap.vendor} selected={selected} setSelected={setSelected} />
                        <Item title="Bid Import Update" to="/datahub/BidImport" icon={<InventoryIcon />} tint={colorsMap.vendor} selected={selected} setSelected={setSelected} />
                    </Collapse>
                    <Divider sx={{ my: 1 }} />

                    {/* ✅ INVENTORY (existing) + requested inspection links added */}
                    <SidebarMenuItem
                        icon={<InventoryIcon sx={{ color: colorsMap.inventory }} />}
                        onClick={() => toggle("inventory")}
                        style={{ color: themeColors.grey[300], paddingLeft: 20 }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Inventory</Typography>
                            {open.inventory ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </Box>
                    </SidebarMenuItem>
                    <Collapse in={open.inventory}>
                        <Item
                            title="Inventory Counts"
                            to="/inventorycounts"
                            icon={<InventoryIcon />}
                            tint={colorsMap.inventory}
                            selected={selected}
                            setSelected={setSelected}
                        />

                        {/* NEW: Inspections under Inventory */}
                        <Item
                            title="Mobile Inspection"
                            to="/mobileinspection"
                            icon={<PhoneIphoneIcon />}
                            tint={colorsMap.inventory}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Inspection Builder"
                            to="/inspectionbuilder"
                            icon={<ConstructionIcon />}
                            tint={colorsMap.inventory}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Inspection Dashboard"
                            to="/inspdashboard"
                            icon={<SpaceDashboardIcon />}
                            tint={colorsMap.inventory}
                            selected={selected}
                            setSelected={setSelected}
                        />
                    </Collapse>
                    <Divider sx={{ my: 1 }} />

                    {/* REPORTS */}
                    <SidebarMenuItem
                        icon={<AssessmentIcon sx={{ color: colorsMap.reports }} />}
                        onClick={() => toggle("reports")}
                        style={{ color: themeColors.grey[300], paddingLeft: 20 }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Reports</Typography>
                            {open.reports ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </Box>
                    </SidebarMenuItem>
                    <Collapse in={open.reports}>
                        <Item title="Finance Reports" to="/finance-reports" icon={<AssessmentIcon />} tint={colorsMap.reports} selected={selected} setSelected={setSelected} />
                    </Collapse>
                    <Divider sx={{ my: 1 }} />

                    {/* ADMINISTRATION */}
                    <SidebarMenuItem
                        icon={<PeopleIcon sx={{ color: colorsMap.admin }} />}
                        onClick={() => toggle("employees")}
                        style={{ color: themeColors.grey[300], paddingLeft: 20 }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Administration</Typography>
                            {open.employees ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </Box>
                    </SidebarMenuItem>
                    <Collapse in={open.employees}>
                        <Item title="Import Students" to="/datahub/ImportStudents" icon={<SupervisorAccountIcon />} tint={colorsMap.admin} selected={selected} setSelected={setSelected} />
                        <Item title="Manage Users" to="/team" icon={<PeopleIcon />} tint={colorsMap.admin} selected={selected} setSelected={setSelected} />
                        <Item title="Integrations" to="/integrations" icon={<PsychologyIcon />} tint={colorsMap.admin} selected={selected} setSelected={setSelected} />
                        <Item title="Employee HR" to="/employee-hr" icon={<SupervisorAccountIcon />} tint={colorsMap.admin} selected={selected} setSelected={setSelected} />
                        <Item title="Locations" to="/schoollocations" icon={<RoomIcon />} tint={colorsMap.admin} selected={selected} setSelected={setSelected} />
                        <Item title="District" to="/districtedit" icon={<HomeIcon />} tint={colorsMap.admin} selected={selected} setSelected={setSelected} />
                    </Collapse>
                    <Divider sx={{ my: 1 }} />

                    {/* FIG AGENT */}
                    <SidebarMenuItem
                        icon={<PersonSearchIcon sx={{ color: colorsMap.ai }} />}
                        onClick={() => toggle("sandbox")}
                        style={{ color: themeColors.grey[300], paddingLeft: 20 }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">FIG Agent</Typography>
                            {open.sandbox ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </Box>
                    </SidebarMenuItem>
                    <Collapse in={open.sandbox}>
                        <Item title="Productivity Chat" to="/figagent" icon={<PsychologyIcon />} tint={colorsMap.ai} selected={selected} setSelected={setSelected} />
                    </Collapse>

                    <Divider sx={{ my: 1 }} />

                    {/* LOGOUT */}
                    <SidebarMenuItem
                        icon={<LogoutIcon sx={{ color: colorsMap.expense }} />}
                        onClick={handleLogout}
                        style={{ color: themeColors.grey[300], paddingLeft: 20, marginTop: "auto" }}
                    >
                        <Typography variant="h6">Logout</Typography>
                    </SidebarMenuItem>
                </Menu>
            </ProSidebar>
        </Box>
    );
};

export default Sidebar;
